import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../../lib/api";
import { formatDate, formatDateTime, formatUSD } from "../../lib/format";
import { toast } from "sonner";
import { CalendarBlank, FileText, CheckCircle, Prohibit, Wrench, Sparkle } from "@phosphor-icons/react";

const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
                   "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

/** Combina reportes creados + cerrados + preventivos ejecutados en un solo timeline por fecha desc. */
function buildTimeline(data) {
  const events = [];
  const ids = new Set();
  for (const r of data.created || []) {
    events.push({ id: r.id + ":created", ts: r.created_at, kind: "creado", report: r });
    ids.add(r.id);
  }
  for (const r of data.closed || []) {
    events.push({ id: r.id + ":closed", ts: r.closed_at, kind: "cerrado", report: r });
  }
  for (const r of data.created || []) {
    if (r.status === "ANULADO" && r.cancelled_at) {
      events.push({ id: r.id + ":cancelled", ts: r.cancelled_at, kind: "anulado", report: r });
    }
  }
  events.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
  return events;
}

export default function HistorialTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/gerencia/history?year=${year}&month=${month}`)
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || "No se pudo cargar el historial"))
      .finally(() => setLoading(false));
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const events = useMemo(() => data ? buildTimeline(data) : [], [data]);

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    return [y - 1, y, y + 1];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const kindMeta = {
    creado:   { color: "#1F3864", bg: "#1F386410", label: "Reporte creado",       Ic: FileText },
    cerrado:  { color: "#10b981", bg: "#10b98110", label: "Orden cerrada",         Ic: CheckCircle },
    anulado:  { color: "#ef4444", bg: "#ef444410", label: "Reporte anulado",       Ic: Prohibit },
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold">Historial</h3>
          <p className="text-xs text-[color:var(--q-muted)]">
            Todo lo que ocurrió cada mes: reportes, órdenes cerradas y preventivos ejecutados. Nada se pierde al cambiar de mes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div>
            <label className="q-label text-[color:var(--q-muted)] block mb-1">Mes</label>
            <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}
                    data-testid="history-month-select"
                    className="h-10 px-3 rounded-lg border border-[color:var(--q-border)] text-sm">
              {MONTHS_ES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="q-label text-[color:var(--q-muted)] block mb-1">Año</label>
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}
                    data-testid="history-year-select"
                    className="h-10 px-3 rounded-lg border border-[color:var(--q-border)] text-sm font-mono">
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Resumen del mes */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="history-summary">
          <div className="q-card p-3">
            <div className="q-label text-[color:var(--q-muted)]">Reportes creados</div>
            <div className="font-mono text-2xl text-[color:var(--q-navy)]">{data.counts.created}</div>
          </div>
          <div className="q-card p-3">
            <div className="q-label text-[color:var(--q-muted)]">Órdenes cerradas</div>
            <div className="font-mono text-2xl text-emerald-600">{data.counts.closed}</div>
          </div>
          <div className="q-card p-3">
            <div className="q-label text-[color:var(--q-muted)]">Anulados</div>
            <div className="font-mono text-2xl text-rose-500">{data.counts.cancelled}</div>
          </div>
          <div className="q-card p-3">
            <div className="q-label text-[color:var(--q-muted)]">Preventivos ejecutados</div>
            <div className="font-mono text-2xl text-[color:var(--q-gold-dark)]">{data.counts.preventive_executed}</div>
          </div>
        </div>
      )}

      {loading && <div className="q-card p-6 text-center text-sm text-[color:var(--q-muted)]">Cargando historial…</div>}

      {!loading && data && events.length === 0 && (
        <div className="q-card p-8 text-center text-sm text-[color:var(--q-muted)]" data-testid="history-empty">
          <CalendarBlank size={32} className="mx-auto mb-3 opacity-40"/>
          Sin eventos registrados en {MONTHS_ES[month - 1]} {year}.
        </div>
      )}

      {!loading && events.length > 0 && (
        <div className="grid gap-2" data-testid="history-events">
          {events.map(ev => {
            const meta = kindMeta[ev.kind];
            const r = ev.report;
            const preventive = r.source === "preventivo";
            return (
              <div key={ev.id} className="q-card p-3 flex flex-wrap sm:flex-nowrap items-start gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                     style={{ background: meta.bg, color: meta.color }}>
                  <meta.Ic size={16} weight="fill"/>
                </div>
                <div className="flex-1 min-w-0 basis-full sm:basis-auto">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold" style={{ color: meta.color }}>{meta.label}</div>
                    {preventive && (
                      <span className="chip chip-media text-[10px] flex items-center gap-1">
                        <Wrench size={9} weight="fill"/> PREVENTIVO
                      </span>
                    )}
                    <span className="text-xs text-[color:var(--q-muted)] font-mono">{formatDateTime(ev.ts)}</span>
                  </div>
                  <div className="text-sm mt-1 break-words">
                    <span className="font-mono font-semibold text-[color:var(--q-navy)]">{r.room || "-"}</span>
                    {" · "}{r.note || "Sin nota"}
                  </div>
                  {ev.kind === "cerrado" && r.estimated_cost > 0 && (
                    <div className="text-xs text-[color:var(--q-muted)] mt-1">
                      Costo: {formatUSD(r.estimated_cost)} · Cerrado por {r.history?.slice(-1)?.[0]?.by || "—"}
                    </div>
                  )}
                  {ev.kind === "anulado" && (
                    <div className="text-xs text-rose-600 mt-1">
                      Anulado por {r.cancelled_by} · Motivo: {r.cancel_reason}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
